import React, { useState, useRef } from "react";
import { EmailTemplate, TemplateProcessingResult } from "../types";

interface CustomTemplateUploadProps {
  accessToken: string;
  onClose: () => void;
  onTemplateSaved: (template: EmailTemplate) => void;
}

type UploadStep = "input" | "processing" | "review" | "saving";

const CustomTemplateUpload: React.FC<CustomTemplateUploadProps> = ({
  accessToken,
  onClose,
  onTemplateSaved,
}) => {
  const [step, setStep] = useState<UploadStep>("input");
  const [rawHtml, setRawHtml] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [processingResult, setProcessingResult] = useState<TemplateProcessingResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".html") && !file.name.endsWith(".htm")) {
      setError("Please upload an HTML file (.html or .htm)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setRawHtml(content);
      setError(null);
    };
    reader.onerror = () => {
      setError("Failed to read file");
    };
    reader.readAsText(file);
  };

  // Process HTML with Claude
  const handleProcess = async () => {
    if (!rawHtml.trim()) {
      setError("Please enter or upload HTML content");
      return;
    }

    // Basic validation
    if (!rawHtml.includes("<") || !rawHtml.includes(">")) {
      setError("Content doesn't appear to be valid HTML");
      return;
    }

    if (rawHtml.length > 500 * 1024) {
      setError("HTML content exceeds 500KB limit");
      return;
    }

    setStep("processing");
    setError(null);

    try {
      const response = await fetch("/api/templates/process", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ raw_html: rawHtml }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to process template");
      }

      const result: TemplateProcessingResult = await response.json();
      setProcessingResult(result);
      setStep("review");
    } catch (err) {
      console.error("Error processing template:", err);
      setError(err instanceof Error ? err.message : "Failed to process template");
      setStep("input");
    }
  };

  // Save template
  const handleSave = async () => {
    if (!processingResult || !templateName.trim()) {
      setError("Please enter a template name");
      return;
    }

    setStep("saving");
    setError(null);

    try {
      const response = await fetch("/api/templates", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: templateName.trim(),
          description: "Custom template",
          html_content: processingResult.processedHtml,
          markers: processingResult.insertionPoints.map((ip) => ip.marker),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to save template");
      }

      const savedTemplate: EmailTemplate = await response.json();
      onTemplateSaved(savedTemplate);
    } catch (err) {
      console.error("Error saving template:", err);
      setError(err instanceof Error ? err.message : "Failed to save template");
      setStep("review");
    }
  };

  return (
    <div className="border border-border-subtle rounded bg-navy/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-rose-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="font-medium text-text-bright">Add Custom Template</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-text-secondary hover:text-text-primary transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-4 text-xs">
          <span className={`px-2 py-1 rounded ${step === "input" ? "bg-rose-accent text-navy-darkest" : "bg-navy-mid text-text-secondary"}`}>
            1. Input HTML
          </span>
          <span className="text-navy-light">→</span>
          <span className={`px-2 py-1 rounded ${step === "processing" ? "bg-rose-accent text-navy-darkest" : step === "review" || step === "saving" ? "bg-navy-mid text-text-primary" : "bg-navy-mid text-text-muted"}`}>
            2. AI Processing
          </span>
          <span className="text-navy-light">→</span>
          <span className={`px-2 py-1 rounded ${step === "review" || step === "saving" ? "bg-rose-accent text-navy-darkest" : "bg-navy-mid text-text-muted"}`}>
            3. Review & Save
          </span>
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-4 p-3 rounded-sm bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-start gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Step: Input */}
        {step === "input" && (
          <div>
            <p className="text-sm text-text-secondary mb-3">
              Paste your HTML email template below or upload an HTML file. Our AI will analyze it and identify where to insert content.
            </p>

            <textarea
              value={rawHtml}
              onChange={(e) => {
                setRawHtml(e.target.value);
                setError(null);
              }}
              placeholder="Paste your HTML template here..."
              className="w-full h-48 px-3 py-2 rounded-sm bg-navy-deeper/50 border border-border-subtle text-text-primary text-sm font-mono placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-rose-accent/30 resize-none"
            />

            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".html,.htm"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-navy-mid/50 text-text-primary hover:bg-navy-mid text-sm transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload .html
                </button>
                {rawHtml && (
                  <span className="text-xs text-text-muted">
                    {(rawHtml.length / 1024).toFixed(1)} KB
                  </span>
                )}
              </div>

              <button
                onClick={handleProcess}
                disabled={!rawHtml.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-sm bg-rose-accent text-navy-darkest hover:bg-rose-bright disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
              >
                Process with AI
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Step: Processing */}
        {step === "processing" && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-12 h-12 border-3 border-rose-accent/30 border-t-rose-accent rounded-full animate-spin mb-4" />
            <p className="text-text-primary font-medium">Analyzing your template...</p>
            <p className="text-sm text-text-muted mt-1">Identifying content areas and optimizing for email clients</p>
          </div>
        )}

        {/* Step: Review */}
        {(step === "review" || step === "saving") && processingResult && (
          <div>
            {/* Preview */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-text-primary mb-2">Preview</h4>
              <div className="h-56 bg-white rounded-sm overflow-hidden border border-border-subtle">
                <iframe
                  srcDoc={processingResult.processedHtml
                    .replace("{{subject}}", "Your Email Subject")
                    .replace("{{content}}", "<p>Your email content will appear here. The AI has identified the best location for your message body.</p>")
                    .replace("{{signature}}", "<p>Best regards,<br/>Your Name</p>")
                    .replace(/\{\{first_name\}\}/g, "John")
                    .replace(/\{\{last_name\}\}/g, "Doe")
                    .replace(/\{\{full_name\}\}/g, "John Doe")}
                  className="w-full h-full border-0"
                  title="Processed Template Preview"
                />
              </div>
            </div>

            {/* Insertion points */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-text-primary mb-2">Content Areas Found</h4>
              <div className="flex flex-wrap gap-2">
                {processingResult.insertionPoints.map((ip, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
                      ip.required
                        ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                        : "bg-navy-mid/50 text-text-secondary border border-border-subtle"
                    }`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-mono">{ip.marker}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Warnings */}
            {processingResult.warnings.length > 0 && (
              <div className="mb-4 p-3 rounded-sm bg-amber-500/10 border border-amber-500/30">
                <h4 className="text-sm font-medium text-amber-400 mb-1">Warnings</h4>
                <ul className="text-xs text-amber-300/80 space-y-1">
                  {processingResult.warnings.map((warning, idx) => (
                    <li key={idx}>• {warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Template name input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-1.5">Template Name</label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="My Custom Template"
                className="w-full px-3 py-2 rounded-sm bg-navy-deeper/50 border border-border-subtle text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-rose-accent/30"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  setStep("input");
                  setProcessingResult(null);
                }}
                disabled={step === "saving"}
                className="flex items-center gap-1.5 px-3 py-2 rounded-sm bg-navy-mid/50 text-text-primary hover:bg-navy-mid text-sm transition-colors disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                </svg>
                Back to Edit
              </button>

              <button
                onClick={handleSave}
                disabled={!templateName.trim() || step === "saving"}
                className="flex items-center gap-2 px-4 py-2 rounded-sm bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
              >
                {step === "saving" ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Save Template
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomTemplateUpload;
